// YoastSeo.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import useYoast from "../hooks/useYoast";

/**
 * Props:
 * - wpUrl: string (e.g. "https://parmazing.net")
 * - endpoint: "posts" | "pages" (default "posts")
 * - slug: post slug (string)
 * - post: optional full post object you already have (will be used for fallbacks and may contain yoast_head_json)
 * - fallback: optional fallback fields { title, description, image, canonical }
 */
export default function YoastSeo({ wpUrl, endpoint = "posts", slug, post = null, fallback = {} }) {
  const { seo, loading } = useYoast({ wpUrl, endpoint, slug, post, cache: true });

  // If Yoast JSON exists, use it, else fallback to heuristics
  const s = seo || {};

  // Simple helper: first non-empty among options
  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "") ?? "";

  const title = pick(s.title, s.og_title, s.twitter_title, fallback.title, post?.title?.rendered && post.title.rendered.replace(/<[^>]*>/g, ""));
  const description = pick(s.description, s.og_description, s.twitter_description, fallback.description, post?.excerpt?.rendered && post.excerpt.rendered.replace(/<[^>]*>/g, ""));
  const canonical = pick(s.canonical, fallback.canonical, post?.link, window?.location?.href);
  const ogImages = s?.open_graph?.images || s?.og_image || s?.og_images || s?.og || [];
  const ogImage = Array.isArray(ogImages) && ogImages.length ? (ogImages[0].url || ogImages[0]) : (fallback.image || (post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url) || "");

  // Twitter card type from Yoast or infer from image presence
  const twitterCard = s?.twitter?.card || (ogImage ? "summary_large_image" : "summary");

  // Structured data
  const schema = s?.schema ?? null;

  // If loading and no seo yet, return null so your existing Helmet remains intact until Yoast resolved
  if (loading && !seo) return null;

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}

      {/* Canonical */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      {title && (<meta name="title" property="og:title" content={title} />)}
      {description && (<meta name="description" property="og:description" content={description} />)}
      {ogImage && (<meta name="image" property="og:image" content={ogImage} />)}
      {s?.og_type && (<meta name="type" property="og:type" content={s.og_type} />)}
      {canonical && (<meta name="url" property="og:url" content={canonical} />)}
      {s?.site_name && (<meta name="site_name" property="og:site_name" content={s.site_name} />)}


      {/* Article timestamps (Yoast sometimes exposes these) */}
      {post?.date && <meta property="article:published_time" content={post.date} />}
      {post?.modified && <meta property="article:modified_time" content={post.modified} />}

      {/* Twitter */}
      {twitterCard && <meta name="twitter:card" content={twitterCard} />}
      {s?.twitter?.site && <meta name="twitter:site" content={s.twitter.site} />}
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {s?.twitter?.creator && <meta name="twitter:creator" content={s.twitter.creator} />}

      {/* Robots */}
      {s?.robots && <meta name="robots" content={s.robots} />}

      {/* Schema */}
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
}
